// /opt/ptg01-v1/front-extranet/components/InstallPrompt.tsx
// Prompt "Installer l'app" — apparait uniquement si :
//   - beforeinstallprompt a été capturé (Android Chrome / Edge)
//   - l'user a déjà visité le site au moins 2 fois (évite le spam au 1er visit)
//   - user non encore installé (standalone mode)
//   - pas dismissé dans les 7 derniers jours
//
// Sur iOS Safari : pas d'event beforeinstallprompt, on affiche à la place une
// instruction "Ajouter à l'écran d'accueil" via le bouton Partager.
'use client';

import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { Download, Share, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const VISIT_COUNT_KEY = 'nx_pwa_visit_count';
/** Émis quand les conditions d'affichage changent (visite comptée, refus enregistré). */
const EVENEMENT_INVITE = 'nx-pwa-invite';

function souscrireInvite(onChange: () => void): () => void {
    window.addEventListener(EVENEMENT_INVITE, onChange);
    return () => window.removeEventListener(EVENEMENT_INVITE, onChange);
}

/** Rendu serveur : aucune condition navigateur connue, donc pas de bannière. */
const faux = () => false;
const DISMISSED_KEY = 'nx_pwa_dismissed_at';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

function isStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        || (window.navigator as any).standalone === true;
}

function isIOS(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

function shouldShow(): boolean {
    if (typeof window === 'undefined') return false;
    if (isStandalone()) return false;
    const visits = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
    if (visits < 2) return false;
    const dismissedAt = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return false;
    return true;
}

export default function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    /** Rejet dans la session courante : masque la bannière sans attendre le stockage. */
    const [rejete, setRejete] = useState(false);

    // Conditions de fréquence (visites, refus récent) et plateforme : lues dans
    // le navigateur, jamais pendant le rendu serveur. La lecture est refaite à
    // chaque EVENEMENT_INVITE — notamment après le comptage de la visite.
    const proposable = useSyncExternalStore(souscrireInvite, shouldShow, faux);
    const surIphone = useSyncExternalStore(souscrireInvite, isIOS, faux);

    // Compte des visites : effet de bord sur le stockage, sans état React. Le
    // compteur mis à jour est ensuite relu par `proposable` via l'événement.
    useEffect(() => {
        if (sessionStorage.getItem('nx_visit_counted')) return;
        const precedent = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
        localStorage.setItem(VISIT_COUNT_KEY, String(precedent + 1));
        sessionStorage.setItem('nx_visit_counted', '1');
        window.dispatchEvent(new Event(EVENEMENT_INVITE));
    }, []);

    // Android / Chrome : l'invite native arrive par événement, la capturer est
    // bien une synchronisation avec l'extérieur — sa place légitime est l'effet.
    useEffect(() => {
        const handler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    // iOS n'émet pas cet événement : l'instruction manuelle est proposée dès que
    // les conditions de fréquence sont réunies. Les deux cas sont dérivés, plus
    // posés dans un état — la bannière ne clignote plus au montage.
    const iosHint = surIphone && proposable;
    const show = !rejete && proposable && (iosHint || deferredPrompt !== null);

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        try {
            await deferredPrompt.prompt();
            await deferredPrompt.userChoice;
        } catch { /* noop */ }
        setDeferredPrompt(null);
        setRejete(true);
    };

    const handleDismiss = () => {
        localStorage.setItem(DISMISSED_KEY, String(Date.now()));
        setRejete(true);
        window.dispatchEvent(new Event(EVENEMENT_INVITE));
    };

    if (!show) return null;

    return (
        <div
            data-testid="pwa-install-prompt"
            className="lg:hidden fixed left-3 right-3 z-[70] bg-white border-2 border-[#000091]/25 rounded-2xl shadow-2xl p-4"
            style={{
                bottom: 'calc(64px + env(safe-area-inset-bottom) + 12px)',
            }}
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#000091] text-white flex items-center justify-center text-[16px] font-black shrink-0">
                    N
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-[14px] font-bold text-slate-900 leading-tight">
                        Installer Nexvia
                    </h3>
                    {iosHint ? (
                        <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">
                            Touchez <Share className="inline w-3.5 h-3.5 mx-0.5 text-[#000091]" /> puis
                            <strong> « Sur l&apos;écran d&apos;accueil »</strong> pour ajouter l&apos;app.
                        </p>
                    ) : (
                        <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">
                            Accès en 1 clic, notifications paie et dépenses à portée de main.
                        </p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                        {!iosHint && (
                            <button
                                onClick={handleInstall}
                                className="inline-flex items-center gap-1.5 h-8 px-3 text-[12px] font-semibold text-white bg-[#000091] hover:bg-[#1e40af] rounded-md"
                            >
                                <Download className="w-3.5 h-3.5" />
                                Installer
                            </button>
                        )}
                        <button
                            onClick={handleDismiss}
                            className="h-8 px-3 text-[12px] font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
                        >
                            Plus tard
                        </button>
                    </div>
                </div>
                <button
                    onClick={handleDismiss}
                    className="p-1 rounded hover:bg-slate-100 shrink-0"
                    aria-label="Fermer"
                >
                    <X className="w-4 h-4 text-slate-400" />
                </button>
            </div>
        </div>
    );
}
